/**
 * Centralised TanStack Query hooks for all server-state fetching.
 * Each hook owns its cache key and fetch logic — components just consume data.
 */
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { UserActivityDetail } from '../types/monitoring';
import type { FiscalYear } from '../utils/fiscal-year';
import { isE2ETestMode } from '../utils/e2e-test-mode';
import { isValidCustomerGroupId } from '../utils/customer-navigation';
import { shouldRetryQuery } from '../utils/query-retry';

// ── Query key constants ──────────────────────────────────────────
export const QK = {
  orders:            (page: number, limit: number, status?: string) =>
    ['orders', page, limit, status ?? 'all'] as const,
  orderById:         (id: number)                  => ['orders', id]          as const,
  orderTimeline:     (id: number)                  => ['orders', id, 'timeline'] as const,
  orderEhubLogs:     (id: number)                  => ['orders', id, 'ehub-logs'] as const,
  customers:         (page: number, filters?: object) => ['customers', page, filters] as const,
  customerById:      (id: number)                  => ['customers', id]       as const,
  customerGroups:    (page: number, limit: number, filters?: { name?: string }) =>
    ['customer-groups', page, limit, filters?.name ?? ''] as const,
  customerGroupById: (id: number)                  => ['customer-groups', id] as const,
  customersByGroup:  (groupId: number, page: number, limit: number) =>
    ['customer-groups', groupId, 'customers', page, limit] as const,
  promotions:        (page: number, type: string, status: string, search?: string) =>
    ['promotions', page, type, status, search ?? ''] as const,
  promotionById:     (id: number, type?: string, searched?: boolean) =>
    ['promotions', id, type ?? '', searched ? '1' : '0'] as const,
  dailyPurchases:    (startDate?: string, endDate?: string) => ['daily-purchases', startDate, endDate] as const,
  ambrAnalysis:      ()                            => ['ambr-analysis']       as const,
  productAnalysis:   (fiscalYear?: FiscalYear) => ['product-analysis', fiscalYear ?? 'lifetime'] as const,
  productOrders:     (sku: string, fiscalYear?: FiscalYear) => ['product-orders', sku, fiscalYear ?? 'lifetime'] as const,
  monitoring:        (hours: number)               => ['monitoring', hours]   as const,
  monitoringUserActivity: (email: string, hours: number) => ['monitoring', 'user-activity', email, hours] as const,
  customerGroupCounts: (ids: number[])             => ['customer-group-counts', ids.join(',')] as const,
  bcPermissionsAudit: ()                          => ['bc-permissions-audit'] as const,
  bcPermissionTemplates: ()                      => ['bc-permission-templates'] as const,
  bcPermissionTemplate: (id: string)              => ['bc-permission-template', id] as const,
  authMe:              (identity = '')             => ['auth-me', identity]   as const,
};

// ── Orders ───────────────────────────────────────────────────────
export function useOrders(page = 1, limit = 50, status = 'all') {
  return useQuery({
    queryKey: QK.orders(page, limit, status),
    queryFn: () => apiClient.getOrders(page, limit, status),
    // Keep list warm when navigating Order Detail → Back (avoids refetch within window)
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrderById(id: number) {
  return useQuery({
    queryKey: QK.orderById(id),
    queryFn: () => apiClient.getOrderById(id),
    enabled: !!id,
  });
}

export function useOrderTimeline(id: number) {
  return useQuery({
    queryKey: QK.orderTimeline(id),
    queryFn: () => apiClient.getOrderTimeline(id),
    enabled: !!id,
  });
}

export function useOrderEhubLogs(id: number, createdAt: string) {
  return useQuery({
    queryKey: QK.orderEhubLogs(id),
    queryFn: () => apiClient.getOrderEhubLogs(id, createdAt),
    enabled: !!id && !!createdAt,
  });
}

// ── Auth persona ─────────────────────────────────────────────────
export function useAuthMe() {
  const { account, isAuthenticated } = useAuth();
  const e2eTestMode = isE2ETestMode();
  const identity = account?.username?.trim() || account?.homeAccountId || '';
  return useQuery({
    queryKey: QK.authMe(identity),
    queryFn: () => apiClient.getAuthMe(),
    enabled: e2eTestMode || (isAuthenticated && Boolean(identity)),
    staleTime: e2eTestMode ? 0 : 5 * 60 * 1000,
    // Keep a successful persona across remounts; E2E still always refetches.
    refetchOnMount: e2eTestMode ? 'always' : false,
    retry: shouldRetryQuery,
  });
}

// ── Customers ────────────────────────────────────────────────────
export function useCustomers(
  page = 1,
  limit = 50,
  filters?: { email?: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: QK.customers(page, filters),
    queryFn: () => apiClient.getCustomers(page, limit, filters),
    enabled: options?.enabled !== false,
  });
}

export function useCustomerById(id: number, emailSearch?: string) {
  return useQuery({
    queryKey: [...QK.customerById(id), emailSearch ?? ''] as const,
    queryFn: () => apiClient.getCustomerById(id, emailSearch),
    enabled: !!id,
  });
}

// ── Customer Groups ──────────────────────────────────────────────
export { isValidCustomerGroupId };

export function useCustomerGroups(
  page = 1,
  limit = 50,
  filters?: { name?: string },
  searchGeneration = 0,
) {
  const appliedNameFilter = filters?.name ?? '';
  return useQuery({
    queryKey: [...QK.customerGroups(page, limit, filters), searchGeneration] as const,
    queryFn: () => apiClient.getCustomerGroups(page, limit, filters),
    placeholderData: (previousData, previousQuery) => {
      if (!previousQuery) {
        return undefined;
      }

      const previousKey = previousQuery.queryKey as ReturnType<typeof QK.customerGroups>;
      const previousNameFilter = previousKey[3] ?? '';
      if (previousNameFilter !== appliedNameFilter) {
        return undefined;
      }

      return keepPreviousData(previousData);
    },
  });
}

export function useCustomerGroupById(id: number) {
  return useQuery({
    queryKey: QK.customerGroupById(id),
    queryFn: () => apiClient.getCustomerGroupById(id),
    enabled: isValidCustomerGroupId(id),
  });
}

export function useCustomersByGroup(groupId: number, page = 1, limit = 50, enabled = true) {
  return useQuery({
    queryKey: QK.customersByGroup(groupId, page, limit),
    queryFn: () => apiClient.getCustomersByGroupId(groupId, page, limit),
    enabled: isValidCustomerGroupId(groupId) && enabled,
  });
}

export function useCustomerGroupCounts(groupIds: number[]) {
  return useQuery({
    queryKey: QK.customerGroupCounts(groupIds),
    queryFn: () => apiClient.getCustomerGroupCounts(groupIds),
    enabled: groupIds.length > 0,
    staleTime: 10 * 60 * 1000, // counts are expensive — cache 10 min
  });
}

// ── Promotions ───────────────────────────────────────────────────
export function usePromotions(
  page = 1,
  limit = 50,
  type: 'coupons' | 'automatic' = 'coupons',
  status: 'all' | 'active' | 'inactive' = 'all',
  search?: string,
) {
  const normalizedSearch = search?.trim() || undefined;
  return useQuery({
    queryKey: QK.promotions(page, type, status, normalizedSearch),
    queryFn: () => apiClient.getPromotions(page, limit, type, status, normalizedSearch),
  });
}

export function usePromotionById(id: number, type?: string, searched?: boolean) {
  return useQuery({
    queryKey: QK.promotionById(id, type, searched),
    queryFn: () => apiClient.getPromotionById(id, type, searched),
    enabled: !!id,
  });
}

// ── Dashboard / Warehouse ────────────────────────────────────────
export function useDailyPurchases(startDate?: string, endDate?: string) {
  return useQuery({
    queryKey: QK.dailyPurchases(startDate, endDate),
    queryFn: () => apiClient.getDailyPurchases(startDate, endDate),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000, // auto-refresh every 5 min
  });
}

export function useAmbrAnalysis() {
  return useQuery({
    queryKey: QK.ambrAnalysis(),
    queryFn: () => apiClient.getAmbrAnalysis(),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000, // auto-refresh every 5 min
  });
}

export function useProductAnalysis(fiscalYear?: FiscalYear) {
  return useQuery({
    queryKey: QK.productAnalysis(fiscalYear),
    queryFn: () => apiClient.getProductAnalysis(fiscalYear),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}

export function useProductOrders(sku: string, enabled: boolean, fiscalYear?: FiscalYear) {
  return useQuery({
    queryKey: QK.productOrders(sku, fiscalYear),
    queryFn: () => apiClient.getProductOrders(sku, fiscalYear),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMonitoring(hours = 24, enabled = true) {
  return useQuery({
    queryKey: QK.monitoring(hours),
    queryFn: () => apiClient.getMonitoring(hours),
    enabled,
    staleTime: 60 * 1000,
    retry: shouldRetryQuery,
    refetchInterval: (query) => (query.state.status === 'success' ? 60 * 1000 : false),
  });
}

export function useMonitoringUserActivity(
  email: string | null,
  hours: number,
  enabled: boolean,
) {
  return useQuery<UserActivityDetail>({
    queryKey: QK.monitoringUserActivity(email ?? '', hours),
    queryFn: () => apiClient.getMonitoringUserActivity(email!, hours),
    enabled: enabled && !!email,
    staleTime: 60 * 1000,
  });
}

// ── BC Permissions Audit (local-only) ────────────────────────────
export function useBcPermissionsAudit() {
  return useQuery({
    queryKey: QK.bcPermissionsAudit(),
    queryFn: () => apiClient.getBcPermissionsAuditUsers(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useBcPermissionTemplates() {
  return useQuery({
    queryKey: QK.bcPermissionTemplates(),
    queryFn: () => apiClient.getBcPermissionTemplates(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBcPermissionTemplate(templateId: string | null) {
  return useQuery({
    queryKey: QK.bcPermissionTemplate(templateId ?? ''),
    queryFn: () => apiClient.getBcPermissionTemplate(templateId!),
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
  });
}
